// File: frontend/src/pages/students/StudentList.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import api, { getImageUrl } from '../../utils/axios';

const StudentList = () => {
  const { school } = useSchool();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search & Filter State
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination State
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1
  });

  // Modal State for Quick Print Receipt
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);

  /**
   * Fetch paginated student list from backend API
   */
  const fetchStudents = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/api/v1/students', {
        params: {
          search,
          class_id: classFilter,
          gender: genderFilter,
          status: statusFilter,
          page,
          limit: 10
        }
      });

      if (response.data?.success) {
        setStudents(response.data.data || []);
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      } else {
        setError(response.data?.message || 'Failed to fetch students list');
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      setError(err.response?.data?.message || err.message || 'Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch on filter or page changes
  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, classFilter, genderFilter, statusFilter]);

  /**
   * Handle search submission
   */
  const handleSearchSubmit = async (e) => {
    try {
      e.preventDefault();
      setPage(1);
      await fetchStudents();
    } catch (err) {
      console.error('Error on search submit:', err);
    }
  };

  /**
   * Handle reset of filters
   */
  const handleResetFilters = async () => {
    try {
      setSearch('');
      setClassFilter('all');
      setGenderFilter('all');
      setStatusFilter('all');
      setPage(1);
    } catch (err) {
      console.error('Error resetting filters:', err);
    }
  };

  /**
   * Fetch and display admission receipt in modal for printing
   * @param {number} studentId - Database ID of student
   */
  const handlePrintReceipt = async (studentId) => {
    try {
      setReceiptLoading(true);
      const response = await api.get(`/api/v1/students/${studentId}/receipt`);
      if (response.data?.success) {
        setSelectedReceipt(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching receipt:', err);
      alert('Failed to load admission receipt');
    } finally {
      setReceiptLoading(false);
    }
  };

  /**
   * Helper function to return Tailwind CSS classes for status badges
   */
  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
      case 'inactive':
        return 'bg-slate-100 text-slate-700 border border-slate-200';
      case 'transferred':
        return 'bg-blue-100 text-blue-800 border border-blue-200';
      case 'graduated':
        return 'bg-purple-100 text-purple-800 border border-purple-200';
      default:
        return 'bg-slate-100 text-slate-700 border border-slate-200';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Header Navigation */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard/admin" className="flex items-center gap-2 hover:opacity-90 transition">
              {school?.logo ? (
                <img src={school.logo} alt="Logo" className="h-10 w-auto max-w-[140px] object-contain" />
              ) : (
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center font-bold text-base shadow-sm">
                  🎓
                </div>
              )}
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Student Directory</h1>
              <p className="text-xs text-slate-500">Manage all registered students</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/students/navigator"
              className="hidden sm:flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-xl text-sm transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Quick Navigator
            </Link>
            <Link
              to="/students/new"
              style={{ backgroundColor: school?.button_color || 'var(--button-color)' }}
              className="text-white font-medium px-4 py-2 rounded-xl text-sm transition shadow-sm hover:brightness-110 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Add Student
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Search and Filters Header */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm mb-6">
          <form onSubmit={handleSearchSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            
            {/* Search Input */}
            <div className="lg:col-span-2 relative">
              <input
                type="text"
                placeholder="Search by student or father name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:bg-white transition"
              />
              <svg className="w-5 h-5 text-slate-400 absolute left-3 top-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Filter by Class */}
            <div>
              <select
                value={classFilter}
                onChange={(e) => {
                  setClassFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:bg-white transition"
              >
                <option value="all">All Classes</option>
                <option value="1">Class 1</option>
                <option value="2">Class 2</option>
                <option value="3">Class 3</option>
                <option value="4">Class 4</option>
                <option value="5">Class 5</option>
                <option value="6">Class 6</option>
                <option value="7">Class 7</option>
                <option value="8">Class 8</option>
                <option value="9">Class 9</option>
                <option value="10">Class 10</option>
                <option value="11">Class 11</option>
                <option value="12">Class 12</option>
              </select>
            </div>

            {/* Filter by Gender */}
            <div>
              <select
                value={genderFilter}
                onChange={(e) => {
                  setGenderFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:bg-white transition"
              >
                <option value="all">All Genders</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Filter by Status */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:bg-white transition"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="transferred">Transferred</option>
                <option value="graduated">Graduated</option>
              </select>
            </div>

          </form>

          {/* Filter Action Buttons */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={handleResetFilters}
              className="text-xs font-semibold text-slate-500 hover:text-slate-700 transition"
            >
              Reset Filters
            </button>
            <div className="text-xs text-slate-500">
              Showing <span className="font-bold text-slate-700">{students.length}</span> of <span className="font-bold text-slate-700">{pagination.total}</span> students
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-800 hover:underline">Dismiss</button>
          </div>
        )}

        {/* Students Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-4">Student ID</th>
                  <th className="px-6 py-4">Photo</th>
                  <th className="px-6 py-4">Student Name</th>
                  <th className="px-6 py-4">Father Name</th>
                  <th className="px-6 py-4">Class</th>
                  <th className="px-6 py-4">Gender</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm font-medium text-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-slate-400">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <svg className="w-8 h-8 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Loading student records...</span>
                      </div>
                    </td>
                  </tr>
                ) : students.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-slate-500">
                      No student records found matching your filters.
                    </td>
                  </tr>
                ) : (
                  students.map((student) => (
                    <tr key={student.id} className="hover:bg-slate-50/80 transition">
                      <td className="px-6 py-4 font-mono font-bold text-slate-800 text-xs">
                        {student.student_id}
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-xs uppercase shadow-inner overflow-hidden border border-slate-200">
                          {student.photo ? (
                            <img src={getImageUrl(student.photo)} alt={student.student_name} className="w-full h-full object-cover" />
                          ) : (
                            <span>{student.student_name ? student.student_name.slice(0, 2).toUpperCase() : 'ST'}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-bold text-slate-900">
                        {student.student_name}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {student.father_name}
                      </td>
                      <td className="px-6 py-4 font-semibold text-slate-800">
                        Class {student.class_id} {student.section ? `(${student.section})` : ''}
                      </td>
                      <td className="px-6 py-4 capitalize text-slate-600">
                        {student.gender || 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${getStatusBadge(student.status)}`}>
                          {student.status || 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/students/${student.id}`)}
                            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-3 py-1.5 rounded-lg text-xs transition"
                          >
                            View
                          </button>
                          <button
                            onClick={() => navigate(`/students/edit/${student.id}`)}
                            className="bg-blue-50 hover:bg-blue-100 text-primary font-medium px-3 py-1.5 rounded-lg text-xs transition"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handlePrintReceipt(student.id)}
                            className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-medium px-3 py-1.5 rounded-lg text-xs transition flex items-center gap-1"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            Receipt
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {!loading && students.length > 0 && (
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <div className="text-xs text-slate-500">
                Page <span className="font-bold text-slate-700">{pagination.page}</span> of <span className="font-bold text-slate-700">{pagination.totalPages}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Previous
                </button>
                <button
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                  className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      </main>

      {/* Quick Print Receipt Modal */}
      {selectedReceipt && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-200 pb-4 mb-4">
              <h3 className="font-bold text-lg text-slate-800">Admission Receipt</h3>
              <button
                onClick={() => setSelectedReceipt(null)}
                className="text-slate-400 hover:text-slate-600 font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-sm text-slate-700">
              <div className="text-center font-bold text-lg text-slate-900 border-b pb-2">
                {selectedReceipt.school?.name}
              </div>
              <div className="flex justify-between text-xs text-slate-500">
                <span>Receipt No: <strong>{selectedReceipt.receipt_no}</strong></span>
                <span>Date: <strong>{selectedReceipt.issue_date}</strong></span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div>Student ID: <strong>{selectedReceipt.student?.student_id}</strong></div>
                <div>Name: <strong>{selectedReceipt.student?.student_name}</strong></div>
                <div>Father Name: <strong>{selectedReceipt.student?.father_name}</strong></div>
                <div>Class: <strong>Class {selectedReceipt.student?.class_id}</strong></div>
                <div>Join Date: <strong>{selectedReceipt.student?.join_date}</strong></div>
                <div>Phone: <strong>{selectedReceipt.student?.phone}</strong></div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                onClick={() => setSelectedReceipt(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl text-sm transition"
              >
                Close
              </button>
              <button
                onClick={() => window.print()}
                className="px-4 py-2 bg-primary hover:bg-blue-600 text-white font-medium rounded-xl text-sm transition shadow-sm"
              >
                Print Receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentList;
